/**
 * Whether conversation recaps are shown, as a single per-DEVICE preference.
 *
 * Not a per-recap dismissal: closing the banner turns recaps off everywhere until the user turns them
 * back on, which is why the setting is reachable from the menu as well as from the banner's ×. An
 * off-switch with no on-switch would be a one-way door.
 *
 * Stored per device rather than on the account deliberately — it is a display choice about one screen,
 * and syncing it would mean a write to the server every time someone closes a banner.
 *
 * Default ON: a recap only exists once a conversation has had enough turns to be worth summarising, so
 * the common case is that showing it is useful. Absent key ⇒ on, so nobody has to be migrated.
 */
const STORAGE_KEY = 'symphony.recap.visible';

export function isRecapVisible(): boolean {
  // SSR and a blocked/absent localStorage both fall back to the default rather than throwing — this is
  // read during render.
  try {
    if (typeof window === 'undefined') {
      return true;
    }
    return window.localStorage.getItem(STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
}

export function setRecapVisible(visible: boolean): void {
  try {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(STORAGE_KEY, visible ? 'true' : 'false');
    // Same-tab listeners: the native `storage` event only fires in OTHER tabs, so a banner closed here
    // would keep rendering in this one until a remount without this.
    window.dispatchEvent(new CustomEvent(RECAP_VISIBILITY_EVENT, { detail: visible }));
  } catch {
    // A device that cannot persist the choice still honours it for the session (React state), which is
    // better than failing the click.
  }
}

export const RECAP_VISIBILITY_EVENT = 'symphony:recap-visibility';
